// src/user/WithdrawalsPage.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { API } from '../config/apiConfig';
import { useNavigate } from 'react-router-dom';
import { shouldShowKYC } from '../utils/kycUtils';
import PaymentAccountManager from '../components/PaymentAccountManager';
import FeeCalculator from '../components/FeeCalculator';

function parseJSON(text) { try { return JSON.parse(text); } catch { return null; } }

function StatusChip({ status }) {
  const color =
    status === 'Paid' ? 'success' :
    status === 'Approved' ? 'info' :
    status === 'Rejected' ? 'error' : 'warning'; // Pending
  return <Chip size="small" color={color} label={status || '—'} variant="outlined" />;
}

export default function WithdrawalsPage() {
  const navigate = useNavigate();
  
  // Shared
  const token = useMemo(
    () => localStorage.getItem('token') || localStorage.getItem('authToken') || '',
    []
  );
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token]
  );

  // User profile and KYC requirements
  const [userProfile, setUserProfile] = useState(null);
  const [kycRequired, setKycRequired] = useState(false);
  
  // Payment account management for non-KYC users
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState(null);
  
  // Fee calculation state
  const [feeData, setFeeData] = useState(null);
  const [showFeeCalculator, setShowFeeCalculator] = useState(false);
  
  // KYC Status state
  const [kycStatus, setKycStatus] = useState({
    verified: false,
    status: 'not_started',
    loading: true,
    error: null
  });

  // Request form state
  const [availableBalance, setAvailableBalance] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // History state
  const [rows, setRows] = useState([]);

  // Loading + toast
  const [loading, setLoading] = useState({ balance: false, history: false });
  const [toast, setToast] = useState({ open: false, severity: 'success', msg: '' });

  // Load user profile and determine KYC requirements
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (token) {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          const userId = decoded.userId;
          
          // Fetch full user profile from API to get CountryOfResidenceId
          const res = await fetch(API.userProfile(userId), { headers });
          const data = await res.json();
          
          console.log('📥 API Response for user profile:', data);
          
          // Check if we have user data (API returns data directly, not wrapped in success/user)
          if (data && data.UserID) {
            console.log('👤 User data received:', {
              CountryCode: data.CountryCode,
              CountryOfResidenceId: data.CountryOfResidenceId,
              CountryOfResidence: data.CountryOfResidence,
              CountryOfOriginID: data.CountryOfOriginID
            });
            
            // Use CountryCode which corresponds to CountryOfResidenceId (country where user lives)
            const profile = {
              id: userId,
              country: data.CountryCode || null, // This is the country code for residence
              countryCode: data.CountryCode || null, // Explicit countryCode field for KYC check
              countryOfResidenceId: data.CountryOfResidenceId || null,
              countryOfResidence: data.CountryOfResidence || null,
              region: data.RegionId || null,
            };
            setUserProfile(profile);
            
            // Determine if KYC is required based on country of residence (now async - checks database)
            const kycInfo = await shouldShowKYC(profile);
            setKycRequired(kycInfo.required);
            
            console.log('🔍 User Profile and KYC Check (from database):', {
              userId,
              countryOfResidenceId: profile.countryOfResidenceId,
              countryOfResidence: profile.countryOfResidence,
              countryCode: profile.country,
              kycRequired: kycInfo.required,
              kycReason: kycInfo.reason,
              kycInfo: kycInfo
            });
            
            // If KYC is not required, set verified to true
            if (!kycInfo.required) {
              setKycStatus({
                verified: true,
                status: 'verified',
                loading: false,
                error: null
              });
            }
          } else {
            console.error('❌ Failed to get user data from API:', data);
            // Fallback to JWT token data
            const profile = {
              id: userId,
              country: decoded.countryCode || decoded.country || null,
              countryCode: decoded.countryCode || decoded.country || null,
              region: decoded.region || null,
            };
            setUserProfile(profile);
            
            const kycInfo = await shouldShowKYC(profile);
            setKycRequired(kycInfo.required);
            
            if (!kycInfo.required) {
              setKycStatus({
                verified: true,
                status: 'verified',
                loading: false,
                error: null
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        // Default to KYC required for safety
        setKycRequired(true);
      }
    };

    loadUserProfile();
  }, [token, headers]);

  // ---- Fetchers -------------------------------------------------
  const fetchKYCStatus = useCallback(async () => {
    // Only fetch KYC status if KYC is required for this region
    if (!kycRequired) {
      setKycStatus({
        verified: true,
        status: 'verified',
        loading: false,
        error: null
      });
      return;
    }
    setKycStatus(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await fetch(API.kycStatus, { headers });
      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch KYC status');
      }
      
      setKycStatus({
        verified: data.verified || false,
        status: data.status || 'not_started',
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching KYC status:', error);
      setKycStatus({
        verified: false,
        status: 'error',
        loading: false,
        error: error.message
      });
    }
  }, [headers]);

  const fetchBalance = useCallback(async () => {
    setLoading(p => ({ ...p, balance: true }));
    try {
      const res = await fetch(API.walletBalance, { headers });
      const raw = await res.text();
      if (!res.ok) throw new Error(raw || `${res.status}`);
      const data = parseJSON(raw) || {};
      setAvailableBalance(typeof data.balance === 'number' ? data.balance : 0);
      setCurrency(data.currency || data.currencyCode || 'USD');
    } catch (e) {
      setToast({ open: true, severity: 'error', msg: `Balance error: ${e.message}` });
    } finally {
      setLoading(p => ({ ...p, balance: false }));
    }
  }, [headers]);


  const fetchHistory = useCallback(async () => {
    setLoading(p => ({ ...p, history: true }));
    try {
      const res = await fetch(API.withdrawalHistory, { headers });
      const raw = await res.text();
      if (!res.ok) throw new Error(raw || `${res.status}`);
      const data = parseJSON(raw) || {};
      setRows(data.withdrawals || []);
    } catch (e) {
      setToast({ open: true, severity: 'error', msg: `History error: ${e.message}` });
    } finally {
      setLoading(p => ({ ...p, history: false }));
    }
  }, [headers]);

  useEffect(() => {
    if (kycRequired) {
      fetchKYCStatus();
    }
    fetchBalance();
    fetchHistory();
  }, [kycRequired, fetchKYCStatus, fetchBalance, fetchHistory]);

  // ---- Form logic -----------------------------------------------
  const withdrawAll = () => setAmount((availableBalance || 0).toFixed(2));

  const validate = () => {
    const a = Number(amount);
    if (!amount) return 'Enter amount';
    if (!Number.isFinite(a) || a <= 0) return 'Enter valid amount';
    if (Number.isFinite(availableBalance) && a > availableBalance) return 'Amount exceeds available balance';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setToast({ open: true, severity: 'warning', msg: err }); return; }

    // KYC status is already checked in the UI, but double-check here
    // Only check KYC if it's required for this user's region
    if (kycRequired && !kycStatus.verified) {
      setToast({ 
        open: true, 
        severity: 'warning', 
        msg: 'Please complete identity verification before requesting withdrawals' 
      });
      return;
    }

    // For non-KYC users, check if payment account is selected
    if (!kycRequired && !selectedPaymentAccount) {
      setToast({ 
        open: true, 
        severity: 'warning', 
        msg: 'Please select a payment account before requesting withdrawals' 
      });
      return;
    }

    setSubmitting(true);
    try {
      // Determine payment method to enforce USD currency for PayPal/Stripe
      const paymentMethod = kycRequired 
        ? 'Stripe' // KYC users use Stripe
        : (selectedPaymentAccount?.PaymentMethodName || selectedPaymentAccount?.paymentMethod);
      
      // IMPORTANT: PayPal and Stripe payouts MUST always be in USD for global users
      // This ensures users worldwide (UK, EU, Australia, Asian countries, etc.) 
      // receive USD payments regardless of their country, as earnings are displayed in USD
      const finalCurrencyCode = (paymentMethod === 'PayPal' || paymentMethod === 'Stripe') 
        ? 'USD' 
        : currency;
      
      const requestBody = {
        amount: Number(amount),
        currencyCode: finalCurrencyCode,
        // Include payment account info for non-KYC users
        ...(kycRequired ? {} : {
          paymentAccountId: selectedPaymentAccount.AccountID,
          paymentMethod: selectedPaymentAccount.PaymentMethodName,
          accountName: selectedPaymentAccount.AccountName,
          accountNumber: selectedPaymentAccount.AccountNumber,
          bankName: selectedPaymentAccount.BankName,
          sortCode: selectedPaymentAccount.SortCode,
          iban: selectedPaymentAccount.IBAN
        })
      };
      
      // 🔍 DEBUG: Log withdrawal request payload for testing
      console.log('🔍 [WithdrawalRequest] Withdrawal Request Payload:', {
        ...requestBody,
        paymentMethod: paymentMethod || (kycRequired ? 'Stripe (KYC)' : selectedPaymentAccount?.PaymentMethodName),
        currencyEnforcement: {
          originalCurrency: currency,
          detectedMethod: paymentMethod || (kycRequired ? 'Stripe' : 'Non-KYC'),
          isPayPalOrStripe: (paymentMethod === 'PayPal' || paymentMethod === 'Stripe' || kycRequired),
          finalCurrencyCode: finalCurrencyCode,
          currencyEnforced: finalCurrencyCode === 'USD'
        }
      });
      
      const body = JSON.stringify(requestBody);
      const res = await fetch(API.withdrawalRequest, { method: 'POST', headers, body });
      const raw = await res.text();
      const data = parseJSON(raw);

      if (res.status === 201 || data?.success) {
        setToast({ open: true, severity: 'success', msg: 'Withdrawal request submitted successfully' });
        setAmount('');
        // Refresh balance + history
        fetchBalance();
        fetchHistory();
      } else {
        const msg = (data && (data.error || data.message)) || raw || 'Failed to submit request';
        throw new Error(msg);
      }
    } catch (e2) {
      setToast({ open: true, severity: 'error', msg: `Submit error: ${e2.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Helper Functions -----------------------------------------
  const renderKYCStatusCard = () => {
    // Don't show KYC status card if KYC is not required for this region
    if (!kycRequired) {
      return (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" component="div">
                Identity Verification
              </Typography>
              <Chip
                icon={<CheckIcon />}
                label="Not Required"
                color="success"
                variant="filled"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Identity verification is not required in your region. You can request withdrawals directly.
            </Typography>

            <Alert severity="success" icon={<CheckIcon />}>
              You can request withdrawals without identity verification in your region.
            </Alert>
          </CardContent>
        </Card>
      );
    }
    const statusConfig = {
      not_started: { 
        label: 'Not Started', 
        color: 'default', 
        icon: <PendingIcon />,
        description: 'Identity verification is required to request withdrawals'
      },
      incomplete: { 
        label: 'Incomplete', 
        color: 'warning', 
        icon: <WarningIcon />,
        description: 'Please complete your identity verification'
      },
      pending: { 
        label: 'Pending', 
        color: 'info', 
        icon: <PendingIcon />,
        description: 'Your verification is being reviewed'
      },
      under_review: { 
        label: 'Under Review', 
        color: 'info', 
        icon: <PendingIcon />,
        description: 'Your verification is under review (1-2 business days)'
      },
      verified: { 
        label: 'Verified', 
        color: 'success', 
        icon: <VerifiedIcon />,
        description: 'Identity verified! You can request withdrawals'
      },
      action_required: { 
        label: 'Action Required', 
        color: 'error', 
        icon: <WarningIcon />,
        description: 'Additional information is required'
      },
      error: { 
        label: 'Error', 
        color: 'error', 
        icon: <WarningIcon />,
        description: 'Unable to verify status'
      }
    };

    const config = statusConfig[kycStatus.status] || statusConfig.not_started;

    return (
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Identity Verification Status</Typography>
            {kycStatus.loading ? (
              <CircularProgress size={24} />
            ) : (
              <Chip
                icon={config.icon}
                label={config.label}
                color={config.color}
                size="large"
                sx={{ fontSize: '1rem', py: 1 }}
              />
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {config.description}
          </Typography>

          {kycStatus.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {kycStatus.error}
            </Alert>
          )}

          {kycStatus.verified && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              Your identity has been verified! You can now request withdrawals.
            </Alert>
          )}
        </CardContent>

        {!kycStatus.verified && !kycStatus.loading && (
          <CardActions>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard/kyc-verification')}
              startIcon={<VerifiedIcon />}
            >
              {kycStatus.status === 'not_started' ? 'Start Verification' : 'Complete Verification'}
            </Button>
            <Button
              variant="outlined"
              onClick={fetchKYCStatus}
              disabled={kycStatus.loading}
              startIcon={kycStatus.loading ? <CircularProgress size={16} /> : null}
            >
              Refresh Status
            </Button>
          </CardActions>
        )}
      </Card>
    );
  };

  // ---- UI --------------------------------------------------------
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* KYC Status Card */}
      {renderKYCStatusCard()}

      {/* Payment Account Manager - Show for all users (payment methods filtered by country via API) */}
      {/* PayPal and other payment methods available in user's country will be shown */}
      {userProfile && (
        <Box sx={{ mb: 3 }}>
          <PaymentAccountManager
            userProfile={userProfile}
            onAccountSelect={setSelectedPaymentAccount}
            selectedAccountId={selectedPaymentAccount?.AccountID}
          />
        </Box>
      )}

      {/* Fee Calculator */}
      {userProfile && (
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Withdrawal Fee Calculator</Typography>
            <Button
              variant="outlined"
              onClick={() => setShowFeeCalculator(!showFeeCalculator)}
              size="small"
            >
              {showFeeCalculator ? 'Hide' : 'Show'} Calculator
            </Button>
          </Box>
          
          {showFeeCalculator && (
            <FeeCalculator
              userProfile={userProfile}
              onFeeCalculated={setFeeData}
            />
          )}
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Left: Request Form */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 2, opacity: (kycRequired ? kycStatus.verified : true) ? 1 : 0.6 }}>
            <Typography variant="h6" gutterBottom>Request Withdrawal</Typography>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="body2" color="text.secondary">Available Balance:</Typography>
              {loading.balance ? (
                <CircularProgress size={16} />
              ) : (
                <Typography variant="body2" fontWeight={600}>
                  {availableBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {kycRequired && !kycStatus.verified && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Identity verification is required to request withdrawals. Please complete verification above.
              </Alert>
            )}

            {/* Payment Account Selection for Non-KYC Users */}
            {!kycRequired && (
              <Box sx={{ mb: 2 }}>
                {selectedPaymentAccount ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Selected Payment Account: {selectedPaymentAccount.AccountName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedPaymentAccount.PaymentMethodName} • {selectedPaymentAccount.AccountNumber}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Please select a payment account above to enable withdrawals.
                  </Alert>
                )}
              </Box>
            )}

            <form onSubmit={submit}>
              <TextField
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                margin="normal"
                required
                disabled={(kycRequired ? !kycStatus.verified : false) || kycStatus.loading}
              />

              <Button 
                onClick={withdrawAll} 
                size="small" 
                sx={{ mt: -1, mb: 1 }}
                disabled={
                  (kycRequired ? !kycStatus.verified : !selectedPaymentAccount) || 
                  kycStatus.loading
                }
              >
                Withdraw All
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                {kycRequired 
                  ? '💳 Payment will be processed through your verified Stripe account in USD'
                  : '💳 Payment will be processed to your selected payment account'
                }
              </Typography>
              
              {/* Inform users about USD currency for PayPal/Stripe */}
              {((!kycRequired && selectedPaymentAccount && 
                 (selectedPaymentAccount.PaymentMethodName === 'PayPal' || selectedPaymentAccount.PaymentMethodName === 'Stripe')) ||
                (kycRequired && kycStatus.verified)) && (
                <Alert severity="info" sx={{ mt: 1, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                  <Typography variant="body2">
                    💵 Payments via PayPal or Stripe are processed in USD. Your earnings are displayed in USD, 
                    and you'll receive the exact USD amount minus processing fees.
                  </Typography>
                </Alert>
              )}

              {/* Fee Information Display */}
              {feeData && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Fee Breakdown: You'll receive {feeData.currencyCode} {feeData.userReceives.toFixed(2)}
                    {feeData.userPaysFees > 0 && (
                      <span> (fees: {feeData.currencyCode} {feeData.userPaysFees.toFixed(2)})</span>
                    )}
                    {feeData.companyAbsorbs > 0 && (
                      <span> - Company absorbs {feeData.currencyCode} {feeData.companyAbsorbs.toFixed(2)}</span>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processing time: {feeData.processingTimeDescription}
                  </Typography>
                </Alert>
              )}

              <Box mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    (kycRequired ? !kycStatus.verified : !selectedPaymentAccount) || 
                    submitting || 
                    kycStatus.loading
                  }
                  startIcon={submitting ? <CircularProgress size={16} /> : null}
                >
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>

        {/* Right: History */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Withdrawal History</Typography>

            {loading.history ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  No withdrawal requests found.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Requested</TableCell>
                      <TableCell>Processed</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.WithdrawalRequestID}>
                        <TableCell>{r.WithdrawalRequestID}</TableCell>
                        <TableCell align="right">
                          {Number(r.Amount).toFixed(2)}
                        </TableCell>
                        <TableCell>{r.CurrencyCode}</TableCell>
                        <TableCell><StatusChip status={r.Status} /></TableCell>
                        <TableCell>
                          {r.RequestedAt ? new Date(r.RequestedAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          {r.ProcessedAt ? new Date(r.ProcessedAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>{r.Notes || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(p => ({ ...p, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
